import React, {useReducer, useEffect, useState} from "react";
import {Routes, Route, useParams, useNavigate, Outlet} from "react-router-dom";
import CancellationReason from "./CancellationReason";
import CancellationVehicleDetails from "./CancellationVehicleDetails";
import CancellationCharges from "./CancellationCharges";
import CancellationReview, { CancellationPersonalT, CancellationAddressT } from "./CancellationReview";
import CancellationResult from './cancellationResult';
import {selectedSubscriptionReducer, selectedSubscriptionInitialState} from "./selectedSubscriptionReducer";
import {cancellationReasonReducer, cancellationReasonInitialState} from "./cancellationReasonReducer";
import {cancellationVehicleReducer, cancellationVehicleInitialState} from "./cancellationVehicleReducer";
import {cancellationChargesReducer, cancellationChargesInitialState} from "./cancellationChargesReducer";
import Warning from "@components/modal/info/Warning";
import {Country} from "@utils/constants/localisation";
import useServiceHook from '@hooks/useServiceHook';
import customerDetails from '@services/customer/CustomerDetails';
import SubscriptionService from '@services/subscription/SubscriptionService';
import {ISubscriptionSummaryItem} from "@components/cards/SubscriptionSummary/SubscriptionSummary";

// Enum for cancellation journey url paths
export enum CancellationPath {
    Root = "",
    VehicleDetails = "vehicle-details",
    CancellationReason = "cancellation-reason",
    Charges = "charges",
    Review = "review",
    Result = "result"
}

export default function CancellationParent() {
    const {custId, country, subsId} = useParams();
    const navigate = useNavigate();

    // Map the `country` URL param (string | undefined) to the Country enum where possible.
    const resolvedCountry: Country = (() => {
        const raw = String(country ?? '');
        // If the raw value matches one of the enum values, use it directly
        if ((Object.values(Country) as string[]).includes(raw)) return raw as Country;
        throw new Error(`Invalid country parameter: ${raw}`);
    })();

    const [selectedSubscriptionState, selectedSubscriptionDispatch] = useReducer(selectedSubscriptionReducer, selectedSubscriptionInitialState);
    const [cancellationReasonState, cancellationReasonDispatch] = useReducer(cancellationReasonReducer, cancellationReasonInitialState);
    const [cancellationVehicleState, cancellationVehicleDispatch] = useReducer(cancellationVehicleReducer, cancellationVehicleInitialState);
    const [cancellationChargesState, cancellationChargesDispatch] = useReducer(cancellationChargesReducer, cancellationChargesInitialState);
    const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("Unknown error");



    // Fetch subscription details whenever subsId changes and write them into the selected subscription reducer
    const [subsLoading, , , customerSubscriptions] = useServiceHook(
        SubscriptionService.getSubscriptions,
        [custId, resolvedCountry],
        true,
        [custId]
    );
    useEffect(() => {
        if (!subsLoading && customerSubscriptions && subsId) {
            // from the lists of subscriptions, find the one that matches subsId
            const subsDetails: ISubscriptionSummaryItem = customerSubscriptions.find(
                (subs: ISubscriptionSummaryItem) => subs.subsId === subsId
            );
            console.debug("Fetched subscription details for cancellation:", subsDetails);
            if (subsDetails) {
                selectedSubscriptionDispatch({
                    type: "SELECT_SUBSCRIPTION",
                    payload: {
                        subsId: subsDetails.subsId,
                        vehicleId: subsDetails.vehicleId || '',
                        selectedPlan: subsDetails.plan,
                        noticeGiven: subsDetails.noticeGiven,
                        onboardVersion: subsDetails.onboardVersion
                    }
                });
            }
        }
    }, [customerSubscriptions]);

    const [custDetailsLoading, , , customerDetailsResp] = useServiceHook(
        customerDetails,
        [custId, country],
        true,
        [custId]
    );
    const [customerInfo, setCustomerInfo] = useState<CancellationPersonalT>({
        firstName: "",
        middleName: "",
        lastName: "",
        email: "",
    });
    const [customerAddr, setCustomerAddr] = useState<CancellationAddressT>({
        street: "",
        city: "",
        state: "",
        postcode: "",
    });

    useEffect(() => {
        if (!custDetailsLoading && customerDetailsResp) {
            const customerData = customerDetailsResp.data;
            setCustomerInfo({
                firstName: customerData.first_name,
                middleName: customerData.middle_name,
                lastName: customerData.last_name,
                email: customerData.email,
            });
            setCustomerAddr({
                street: customerData.street,
                city: customerData.city,
                state: customerData.state,
                postcode: customerData.postcode,
            });
        }
    }, [custDetailsLoading, customerDetailsResp]);

    function buildCancellationPath(targetComponent: CancellationPath) {
        return `/cancellation-v2/${country}/${custId}/${subsId}/${targetComponent}`;
    }

    // Validate all required states up to and including the current route
    function validateJourneyState(window_path: string): string | null {
        // Always check subscription for all steps
        if (
            window_path.endsWith(`/${CancellationPath.VehicleDetails}`) ||
            window_path.endsWith(`/${CancellationPath.CancellationReason}`) ||
            window_path.endsWith(`/${CancellationPath.Charges}`) ||
            window_path.endsWith(`/${CancellationPath.Review}`) ||
            window_path.endsWith(`/${CancellationPath.Result}`)
        ) {
            if (!selectedSubscriptionState.selectedSubsId) return "No selected subscription found.";
        }
        // Check vehicle details for all steps after vehicle-details
        if (
            window_path.endsWith(`/${CancellationPath.CancellationReason}`) ||
            window_path.endsWith(`/${CancellationPath.Charges}`) ||
            window_path.endsWith(`/${CancellationPath.Review}`) ||
            window_path.endsWith(`/${CancellationPath.Result}`)
        ) {
            if (!cancellationVehicleState.vehicleId || cancellationVehicleState.vehicleId.trim().length === 0)
                return "No vehicle details found.";
            // GB captures where the vehicle is being returned to; AU has no equivalent
            // Fleetio field, so only GB journeys are bounced back for it (TP-6849).
            if (resolvedCountry === Country.GB &&
                (!cancellationVehicleState.hub || cancellationVehicleState.hub.trim().length === 0))
                return "No vehicle return location found.";
        }
        // Check personal/address details for all steps after personal-details
        if (
            window_path.endsWith(`/${CancellationPath.CancellationReason}`) ||
            window_path.endsWith(`/${CancellationPath.Charges}`) ||
            window_path.endsWith(`/${CancellationPath.Review}`) ||
            window_path.endsWith(`/${CancellationPath.Result}`)
        ) {
            if (!customerInfo.email || customerInfo.email.trim().length === 0)
                return "No personal details found.";
            if (!customerAddr.postcode || customerAddr.postcode.trim().length === 0)
                return "No address details found.";
        }
        // Check cancellation reason for all steps after cancellation-reason
        if (
            window_path.endsWith(`/${CancellationPath.Charges}`) ||
            window_path.endsWith(`/${CancellationPath.Review}`) ||
            window_path.endsWith(`/${CancellationPath.Result}`)
        ) {
            if (!cancellationReasonState.cancellationType || cancellationReasonState.cancellationType.trim().length === 0)
                return "No cancellation reason found.";
        }
        return null;
    }

    // Validate state is set, when the path changes
    useEffect(() => {
        const error = validateJourneyState(window.location.hash);
        if (error) {
            setErrorMessage(error);
            setShowErrorModal(true);
        } else {
            setShowErrorModal(false);
            setErrorMessage("");
        }
    }, [window.location.pathname]);

    function handleStateErrorModalClose() {
        // If state error, sends the user back to the start of the journey
        setShowErrorModal(false);
        navigate(buildCancellationPath(CancellationPath.Root));
    }



    return (
        <>
            {showErrorModal && (
                <Warning
                    message={errorMessage}
                    additionalInfo={"The Cancellation Journey has encountered an error. Missing state from a previous step. You must restart the journey."}
                    onClickClose={handleStateErrorModalClose}
                />
            )}
            <Routes>
                <Route
                    path={CancellationPath.Root}
                    element={
                        <CancellationVehicleDetails
                            navigateTarget={buildCancellationPath(CancellationPath.CancellationReason)}
                            selectedSubscriptionState={selectedSubscriptionState}
                            selectedSubscriptionDispatch={selectedSubscriptionDispatch}
                            cancellationVehicleState={cancellationVehicleState}
                            cancellationVehicleDispatch={cancellationVehicleDispatch}
                        />
                    }
                />
                <Route
                    path={CancellationPath.VehicleDetails}
                    element={
                        <CancellationVehicleDetails
                            navigateTarget={buildCancellationPath(CancellationPath.CancellationReason)}
                            selectedSubscriptionState={selectedSubscriptionState}
                            selectedSubscriptionDispatch={selectedSubscriptionDispatch}
                            cancellationVehicleState={cancellationVehicleState}
                            cancellationVehicleDispatch={cancellationVehicleDispatch}
                        />
                    }
                />
                <Route
                    path={CancellationPath.CancellationReason}
                    element={
                        <CancellationReason
                            navigateTarget={buildCancellationPath(CancellationPath.Charges)}
                            cancellationReasonState={cancellationReasonState}
                            cancellationReasonDispatch={cancellationReasonDispatch}
                        />
                    }
                />
                <Route
                    path={CancellationPath.Charges}
                    element={
                        <CancellationCharges
                            navigateTarget={buildCancellationPath(CancellationPath.Review)}
                            selectedSubscriptionState={selectedSubscriptionState}
                            cancellationVehicleState={cancellationVehicleState}
                            cancellationChargesState={cancellationChargesState}
                            cancellationChargesDispatch={cancellationChargesDispatch}
                            cancellationReasonState={cancellationReasonState}
                            country={resolvedCountry}
                        />
                    }
                />
                <Route
                    path={CancellationPath.Review}
                    element={
                        <CancellationReview
                            navigateTarget={buildCancellationPath(CancellationPath.Result)}
                            selectedSubscriptionState={selectedSubscriptionState}
                            cancellationReasonState={cancellationReasonState}
                            cancellationVehicleState={cancellationVehicleState}
                            cancellationPersonal={customerInfo}
                            cancellationAddress={customerAddr}
                            cancellationChargesState={cancellationChargesState}
                        />
                    }
                />
                <Route
                    path={CancellationPath.Result}
                    element={
                        <CancellationResult
                            selectedSubscriptionState={selectedSubscriptionState}
                            cancellationReasonState={cancellationReasonState}
                            cancellationVehicleState={cancellationVehicleState}
                            cancellationChargesState={cancellationChargesState}
                            country={resolvedCountry}
                        />
                    }
                />
            </Routes>
            <Outlet/>
        </>
    );
};
